import { FC, useEffect } from "react";
import {
  BookingDataType,
  BookingHolidayPackage,
  isHolidayPackage,
} from "data/interfaces";
import { useGetHolidayPackageQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import { isWithinLatestBooking } from "application/utils/latestBooking";
import InRequestWindowPanel from "./InRequestWindowPanel";

interface Props {
  booking: BookingDataType;
  onSendRequest: (item: BookingDataType) => Promise<void> | void;
  onRemove: (item: BookingDataType) => void;
  onChangeDates: (item: BookingDataType) => void;
  isSendingRequest: boolean;
  onProblematicChange: (id: string | number, isProblematic: boolean) => void;
}

/**
 * Renders the "Within request window" panel under a holiday package card
 * when its start date is within the package's `latest_booking` window.
 *
 * Fetches the live `latest_booking` value from the server on mount (with
 * refetch-on-mount enabled) so host-side edits take effect immediately.
 * Falls back to the snapshot baked in at add-to-trip time while the fetch
 * is in flight.
 *
 * This component renders ONLY the panel — the parent provides the
 * surrounding card. Reports its `isProblematic` state up via callback so
 * the parent can drive a "Continue"/"Pay" gate.
 *
 * The early return is placed after all hooks to satisfy the Rules of Hooks.
 */
const HolidayPackagePanelGuard: FC<Props> = ({
  booking,
  onSendRequest,
  onRemove,
  onChangeDates,
  isSendingRequest,
  onProblematicChange,
}) => {
  // Narrow the union; guards are run after hooks (see end of component).
  const isHolidayPkg = isHolidayPackage(booking.item);
  const hpBooking = isHolidayPkg
    ? (booking as BookingHolidayPackage)
    : null;
  const pkg = hpBooking?.item;
  const uuid = pkg ? String(pkg.package_uuid) : "";

  // Always call the hook so the hook order is stable across renders.
  // `skip` prevents the request when this row isn't a holiday package.
  // `refetchOnMountOrArgChange: true` bypasses the cache populated when
  // the user originally visited the package detail page, so DB edits
  // propagate.
  const { data: serverData } = useGetHolidayPackageQuery(uuid, {
    skip: !isHolidayPkg,
    refetchOnMountOrArgChange: true,
  });

  // The transformResponse on useGetHolidayPackageQuery nests the package
  // under data.holidayPackage.
  const serverPackage =
    (serverData as { holidayPackage?: { latest_booking?: string } } | undefined)
      ?.holidayPackage;
  const serverLatest = serverPackage?.latest_booking;
  const effectiveLatestBooking =
    serverLatest !== undefined && pkg ? serverLatest : pkg?.latest_booking;

  const isProblematic = !!(
    hpBooking &&
    isWithinLatestBooking(hpBooking.startDate, effectiveLatestBooking)
  );

  useEffect(() => {
    if (!hpBooking) return;
    onProblematicChange(hpBooking.id, isProblematic);
    return () => onProblematicChange(hpBooking.id, false);
  }, [isProblematic, hpBooking, onProblematicChange]);

  // After all hooks, the early return for non-holiday-package types.
  if (!hpBooking) {
    return null;
  }

  if (!isProblematic) {
    return null;
  }

  return (
    <InRequestWindowPanel
      item={hpBooking}
      onSendRequest={onSendRequest}
      onRemove={onRemove}
      onChangeDates={onChangeDates}
      isLoading={isSendingRequest}
    />
  );
};

export default HolidayPackagePanelGuard;
